/**
 * Значения `CustomModuleBaseTypeEnum` в URL (`baseModuleType`) и в списке выбора.
 * Порядок: Новости → Реестр → Опросы → Обращение → Выступления → модули мероприятия.
 *
 * MY_SPEECH — пресет, который в бэкенде сохраняется как `News` с зарезервированным
 * слагом `my_speech`. Использует существующий маршрут MY_SPEECH со страницей
 * модерации заявок (`approveSpeechNews`/`rejectSpeechNews`).
 *
 * EVENT_PASSPORT / EVENT_PROGRAM / EVENT_REGULATION — пресеты мероприятия; в бэкенде
 * сохраняются как `ObjectPassport` с разными slug-префиксами.
 */
export const WORKSPACE_MODULE_BASE_TYPES = [
  'NEWS',
  'SUPPORTERS',
  'SURVEY',
  'MAIL',
  'MY_SPEECH',
  'EVENT_PASSPORT',
  'EVENT_PROGRAM',
  'EVENT_REGULATION',
] as const

export type WorkspaceModuleBaseType = (typeof WORKSPACE_MODULE_BASE_TYPES)[number]

export const WORKSPACE_MODULE_CREATE_EVENT_PRESETS_ENABLED = true

export const WORKSPACE_MODULE_EVENT_PRESET_TYPES = [
  'EVENT_PASSPORT',
  'EVENT_PROGRAM',
  'EVENT_REGULATION',
] as const satisfies readonly WorkspaceModuleBaseType[]

export type WorkspaceModuleEventPresetType = (typeof WORKSPACE_MODULE_EVENT_PRESET_TYPES)[number]

export function isEventModulePresetBaseType(
  value: WorkspaceModuleBaseType,
): value is WorkspaceModuleEventPresetType {
  return (WORKSPACE_MODULE_EVENT_PRESET_TYPES as readonly string[]).includes(value)
}

export const WORKSPACE_MODULE_POPUP_BASE_TYPES = WORKSPACE_MODULE_CREATE_EVENT_PRESETS_ENABLED
  ? WORKSPACE_MODULE_BASE_TYPES
  : WORKSPACE_MODULE_BASE_TYPES.filter(
      id => !isEventModulePresetBaseType(id),
    )

export function isWorkspaceModuleBaseType(value: unknown): value is WorkspaceModuleBaseType {
  return (
    typeof value === 'string'
    && (WORKSPACE_MODULE_BASE_TYPES as readonly string[]).includes(value)
  )
}

/**
 * Префикс слагов модуля «Выступления». Слаг каждого экземпляра пресета имеет
 * вид `my_speech` (канонический, BC) или `my_speech_<уникальный-суффикс>`.
 */
export const MY_SPEECH_SLUG_PREFIX = 'my_speech'

/** Канонический слаг (для backwards-compat и существующих новостей). */
export const MY_SPEECH_MODULE_SLUG = MY_SPEECH_SLUG_PREFIX

/** Проверяет, является ли слаг модулем «Выступления» (канонический или с суффиксом). */
export function isMySpeechSlug(slug: string | null | undefined): boolean {
  if (!slug) {
    return false
  }
  return slug === MY_SPEECH_SLUG_PREFIX || slug.startsWith(`${MY_SPEECH_SLUG_PREFIX}_`)
}

/** Уникальный слаг для нового экземпляра пресета «Выступления». */
export function generateMySpeechSlug(): string {
  return `${MY_SPEECH_SLUG_PREFIX}_${Date.now()}`
}

/** Префикс слагов модуля «Паспорт мероприятия». */
export const EVENT_PASSPORT_SLUG_PREFIX = 'event_passport'

/** Префикс слагов модуля «Программа». */
export const EVENT_PROGRAM_SLUG_PREFIX = 'event_program'

/** Префикс слагов модуля «Регламент». */
export const EVENT_REGULATION_SLUG_PREFIX = 'event_regulation'

/**
 * @deprecated Legacy-префикс «Круглый стол»; поддерживается для существующих модулей.
 */
export const OBJECT_PASSPORT_SLUG_PREFIX = 'object_passport'

function matchesSlugPrefix(slug: string, prefix: string): boolean {
  return slug === prefix || slug.startsWith(`${prefix}_`)
}

/** Проверяет, является ли слаг модулем «Паспорт мероприятия» (включая legacy `object_passport`). */
export function isEventPassportSlug(slug: string | null | undefined): boolean {
  if (!slug) {
    return false
  }
  return matchesSlugPrefix(slug, EVENT_PASSPORT_SLUG_PREFIX)
    || matchesSlugPrefix(slug, OBJECT_PASSPORT_SLUG_PREFIX)
}

/** Проверяет, является ли слаг модулем «Программа». */
export function isEventProgramSlug(slug: string | null | undefined): boolean {
  if (!slug) {
    return false
  }
  return matchesSlugPrefix(slug, EVENT_PROGRAM_SLUG_PREFIX)
}

/** Проверяет, является ли слаг модулем «Регламент». */
export function isEventRegulationSlug(slug: string | null | undefined): boolean {
  if (!slug) {
    return false
  }
  return matchesSlugPrefix(slug, EVENT_REGULATION_SLUG_PREFIX)
}

/** Любой модуль мероприятия (паспорт / программа / регламент). */
export function isEventModuleSlug(slug: string | null | undefined): boolean {
  return isEventPassportSlug(slug)
    || isEventProgramSlug(slug)
    || isEventRegulationSlug(slug)
}

/** @deprecated Используйте `isEventPassportSlug`. */
export function isObjectPassportSlug(slug: string | null | undefined): boolean {
  return isEventPassportSlug(slug)
}

export function generateEventPassportSlug(): string {
  return `${EVENT_PASSPORT_SLUG_PREFIX}_${Date.now()}`
}

export function generateEventProgramSlug(): string {
  return `${EVENT_PROGRAM_SLUG_PREFIX}_${Date.now()}`
}

export function generateEventRegulationSlug(): string {
  return `${EVENT_REGULATION_SLUG_PREFIX}_${Date.now()}`
}

/** @deprecated Используйте `generateEventPassportSlug`. */
export function generateObjectPassportSlug(): string {
  return generateEventPassportSlug()
}

/**
 * Встроенные слаги: кастомный модуль не может занять тот же `moduleSlug`
 * (уникальный строковый идентификатор в проекте).
 */
export const RESERVED_PROJECT_MODULE_SLUGS = new Set([
  'supporters',
  'about_project',
  'project_members',
  'project_management',
  'project_news',
  'project_survey',
  'question_to_deputy',
  'service_phones',
  'consumer_rights',
  'accident_guidance',
  'entertainment',
])

/** `^[a-z][a-z0-9_]*$`, до 100 символов */
export const PROJECT_MODULE_SLUG_REGEX = /^[a-z][a-z0-9_]*$/

/** @deprecated Используйте `WORKSPACE_MODULE_BASE_TYPES` / `WorkspaceModuleBaseType` */
export const WORKSPACE_MODULE_CREATE_KIND_IDS = WORKSPACE_MODULE_BASE_TYPES

/** @deprecated Используйте `WorkspaceModuleBaseType` */
export type WorkspaceModuleCreateKind = WorkspaceModuleBaseType

/** @deprecated Используйте `isWorkspaceModuleBaseType` */
export function isWorkspaceModuleCreateKind(value: unknown): value is WorkspaceModuleCreateKind {
  return isWorkspaceModuleBaseType(value)
}
